import { defineStore } from "pinia";
import client from "../httpClient/client";
import utils from "../utils/utils";
import { useRootStore } from "./root";
import {
  ConsentModalPayload,
  ConsentModalResponse,
  DynamicHash,
  SearchField,
  SearchState,
  SearchValuesArrayObject,
  PriceField,
} from "types";

export const useSearchStore = defineStore("search", {
  state: (): SearchState => ({
    previousPage: "",
    paymentIntervalFilter: false,
    hasAdditionalFilter: false,
    searchFilters: {},
    customFilters: [],
    searchFields: [],
    additionalFilters: {},
    hasCustomFilters: false,
    productTypeSearch: {},
    hasProductType: false,
    bookingToken: "",
    lastFilters: {},
    lastSuccessfulSearchFingerprint: "",
    lastSuccessfulSearchDay: "",
    lastSuccessfulSearchSnapshot: null,
    preferCachedSearchResults: false,
    suppressSearchRemountNoise: false,
    searchFieldValues: {},
    priceIntervalField: {},
    extraSearchValues: [],
    visibleSearchFields: {},
    searchValuesManager: {},
    hasSearchMaskChanged: false,
    originalSearchFilters: {},
    searchFieldsValidity: {},
    tariffPriceFromTariffManager: {},
    wizardSearchSummary: [],
    campaign: "",
    resourceTypeOptions: [],
    selectedResourceType: [],
    searchPageNumber: 1,
    wizardOverlayOpened: false,
    paymentCoverageFeature: false,
    acceptInformative: false,
    originalPaymentInterval: {},
    originalPaymentLabel: "",
    canUpdateSummary: true,
    wizardSummaryFields: [],
    searchMappingFields: {},
    contractRenewal: "",
    appAlias: "",
    appId: "",
    searchFormSlug: "",
    allowRequestQuote: false,
    insurerFilter: false,
    sortByInsurer: false,
    sortByPrice: true,
    sortByRating: false,
    sortType: "",
  }),
  getters: {
    visibleSearchFieldValues: (state) => {
      const visibleValues: DynamicHash[] = [];
      for (const key in state.searchFieldValues) {
        if (state.visibleSearchFields[key]?.isVisible === true) {
          visibleValues.push({
            type: key,
            value: state.searchFieldValues[key],
          });
        }
        if (key === "r") {
          visibleValues.push({
            type: "r",
            value: state.searchFieldValues[key],
          });
        }
        if (key === "theme") {
          visibleValues.push({
            type: key,
            value: state.searchFieldValues[key],
          });
        }
        if (key === "preview") {
          visibleValues.push({
            type: key,
            value: state.searchFieldValues[key],
          });
        }
      }
      state.searchFields.forEach((field) => {
        visibleValues.forEach((value) => {
          if (field.attributes.alias_name === value.type) {
            value.position = field.attributes.position;
            value.field_type = field.attributes.type;
            value.search_field_id = field.id;
            value.name = field.attributes.name_de;
            if (
              field.attributes.type === "collection" &&
              field.attributes.resource_type_id !== null &&
              field.attributes.search_field_values.length === 0
            ) {
              const selected = state.selectedResourceType?.[field.id];
              const opt = state.resourceTypeOptions?.[field.id];
              const def = field.attributes.default_value;
              if (selected && selected.id) {
                value.value = selected.id;
                value.display_value = selected.label;
              } else {
                if (opt && opt.id && opt.label && value.value === opt.label) {
                  value.value = opt.id;
                  value.display_value = opt.label;
                }
                if (def && def.id && def.label && value.value === def.label) {
                  value.value = def.id;
                  value.display_value = def.label;
                }
              }
            }
            if (field.attributes.type === "collection") {
              if (!Array.isArray(value.value) && value.value) {
                const filterSelectOption =
                  field.attributes.search_field_values.filter((option) => {
                    return option.id.toString() === value.value.toString();
                  });
                if (filterSelectOption[0]?.label === "true") {
                  value.display_value = "Ja";
                }
                if (filterSelectOption[0]?.label === "false") {
                  value.display_value = "Nein";
                } else {
                  value.display_value = filterSelectOption[0]?.label;
                }
              }
              if (Array.isArray(value.value) && value.value.length) {
                const valueArr = value.value as string[];
                const searchValueObject = field.attributes.search_field_values
                  .filter((v) => {
                    return valueArr.includes(v.id.toString());
                  })
                  .map((v) => {
                    return v.label;
                  });
                value.display_value = searchValueObject.join(",");
              }
            }
          }
        });
      });
      return visibleValues;
    },
    sortedSearchFields: (state) => {
      return state.searchFields.sort((fieldA, fieldB) => {
        return fieldA.attributes.position - fieldB.attributes.position;
      });
    },
    insuranceTypes(): SearchField[] {
      return this.sortedSearchFields.filter((field) => {
        return (
          field.attributes.attribute_name ===
          "coverage_features_travel_product_type"
        );
      });
    },
    travelPersonType: (state) => {
      return state.searchFieldValues.person_type;
    },
  },
  actions: {
    setPaymentCoverageFeature(value) {
      this.paymentCoverageFeature = value;
    },
    setWizardOverlayOpened(value) {
      this.wizardOverlayOpened = value;
    },
    setPreviousPage(value) {
      this.previousPage = value;
    },
    setselectedResourceType(value) {
      this.selectedResourceType = value;
    },
    setCampaign(value) {
      this.campaign = value;
    },
    setResourceTypeOptions(choice) {
      // Resource options must be keyed by search field id (e.g. "168").
      // Passing a bare option `{ id, label }` used to wipe the map and break breed
      // resolution after remount / back-from-booking.
      if (choice == null || Array.isArray(choice)) {
        this.resourceTypeOptions = {};
        return;
      }
      if (typeof choice !== "object") {
        this.resourceTypeOptions = {};
        return;
      }
      const cleaned: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(choice)) {
        if (/^\d+$/.test(key) && value && typeof value === "object") {
          cleaned[key] = value;
        }
      }
      this.resourceTypeOptions = cleaned;
    },
    upsertResourceTypeOption(fieldId: string | number, option: unknown) {
      const map: Record<string, unknown> = {};
      const current = this.resourceTypeOptions;
      if (current && typeof current === "object" && !Array.isArray(current)) {
        for (const [key, value] of Object.entries(current)) {
          if (/^\d+$/.test(key) && value && typeof value === "object") {
            map[key] = value;
          }
        }
      }
      if (option && typeof option === "object") {
        map[String(fieldId)] = {
          ...(option as Record<string, unknown>),
          search_field_id:
            (option as { search_field_id?: string | number }).search_field_id ??
            fieldId,
        };
      }
      this.resourceTypeOptions = map;
    },
    setAdditionalFilters(filter) {
      this.additionalFilters = filter;
    },
    setProductTypeSearch(filter) {
      this.productTypeSearch = filter;
    },
    setInsurerFilter(filter) {
      this.insurerFilter = filter;
    },
    setSortByInsurer(sort) {
      this.sortByInsurer = sort;
    },
    setSortByPrice(sort) {
      this.sortByPrice = sort;
    },
    setSortByRating(sort) {
      this.sortByRating = sort;
    },
    setSortType(val) {
      this.sortType = val;
    },
    setAllowRequestQuote(val) {
      this.allowRequestQuote = val;
    },
    setHasAdditionalFilter() {
      this.hasAdditionalFilter = true;
    },
    setPaymentIntervalFilter() {
      this.paymentIntervalFilter = true;
    },
    setSearchFields(fields: SearchField[]) {
      this.searchFields = fields;
    },
    setAcceptInformative(data) {
      this.acceptInformative = data;
    },
    setCanUpdateSummary(val) {
      this.canUpdateSummary = val;
    },
    sethasProductType(value) {
      this.hasProductType = value;
    },
    setsearchPageNumber(page) {
      this.searchPageNumber = page;
    },
    setOriginalPaymentInterval(choice) {
      this.originalPaymentInterval = choice;
    },
    setextraSearchValues(filter) {
      this.extraSearchValues = filter;
    },
    setPriceIntervalField(fieldData: PriceField) {
      this.priceIntervalField = fieldData;
    },
    setCustomFilters(filter) {
      this.customFilters = filter;
    },
    setContractRenewal(data) {
      this.contractRenewal = data;
    },
    setWizardSearchSummary(data) {
      this.wizardSearchSummary = data;
    },
    setDefaultSearchFieldValues(
      fields: SearchField[],
      shouldSetDefaultValues: boolean,
    ) {
      if (!shouldSetDefaultValues) {
        fields.forEach((field) => {
          if (
            !this.searchFieldValues[field.attributes.alias_name] &&
            field.attributes.attribute_name ===
              "coverage_features_travel_product_type"
          ) {
            this.searchFieldValues[field.attributes.alias_name] = [];
          } else {
            this.searchFieldValues[field.attributes.alias_name] = "";
          }
        });
        return;
      }
      fields.forEach((field) => {
        if (
          !this.searchFieldValues[field.attributes.alias_name] &&
          field.attributes.attribute_name ===
            "coverage_features_travel_product_type"
        ) {
          this.searchFieldValues[field.attributes.alias_name] =
            typeof field.attributes.default_value === "object"
              ? field.attributes.default_value
              : field.attributes.default_value.toString().split(",");
        }

        if (
          (!this.searchFieldValues[field.attributes.alias_name] &&
            field.attributes.type === "collection" &&
            field.attributes.resource_type_id === null &&
            field.attributes.search_field_values.length !== 0) ||
          (!this.searchFieldValues[field.attributes.alias_name] &&
            field.attributes.type === "collection" &&
            field.attributes.resource_type_id !== null &&
            field.attributes.search_field_values.length !== 0)
        ) {
          this.searchFieldValues[field.attributes.alias_name] =
            field.attributes.default_value;
        }
        if (
          !this.searchFieldValues[field.attributes.alias_name] &&
          field.attributes.resource_type_id !== null &&
          field.attributes.search_field_values.length === 0
        ) {
          if (field.attributes.default_value.label) {
            this.searchFieldValues[field.attributes.alias_name] =
              field.attributes.default_value.label;
          } else {
            this.searchFieldValues[field.attributes.alias_name] =
              field.attributes.default_value;
          }
        }
        if (
          !this.searchFieldValues[field.attributes.alias_name] &&
          (field.attributes.type === "text" ||
            field.attributes.type === "number")
        ) {
          const isTruthy =
            typeof field.attributes.default_value === "number" ||
            Boolean(field.attributes.default_value);
          this.searchFieldValues[field.attributes.alias_name] = isTruthy
            ? field.attributes.default_value
            : "";
        }
        if (
          field.attributes.type === "date" &&
          utils.isValidDate(field.attributes.default_value.toString())
        ) {
          this.searchFieldValues[field.attributes.alias_name] =
            field.attributes.default_value;
        }
      });
    },
    setInitialSearchFieldsValidity(fieldsArray: SearchField[]) {
      const fieldsValidity = {};
      for (let i = 0; i < fieldsArray.length; i++) {
        fieldsValidity[fieldsArray[i].attributes.alias_name] = {
          hasErrors: false,
          message: "",
        };
      }
      this.searchFieldsValidity = fieldsValidity;
    },
    setSearchFieldValues(values: SearchValuesArrayObject) {
      this.searchFieldValues = values;
    },
    setDefaultFieldVisibility(fields: SearchField[]) {
      fields.forEach((field) => {
        this.visibleSearchFields[field.attributes.alias_name] =
          !field.attributes.conditions;
      });
    },
    setSearchMappingFields(data) {
      this.searchMappingFields = data;
    },
    setVisibilityFieldState(payload) {
      this.visibleSearchFields = payload;
    },
    setSearchFilters(filters) {
      this.searchFilters = filters;
    },
    setTariffPriceFromTariffManager(data) {
      this.tariffPriceFromTariffManager = data;
    },
    setOriginalSearchFilters(filters) {
      this.originalSearchFilters = filters;
    },
    setAppAlias(txt) {
      this.appAlias = txt;
    },
    setAppId(txt) {
      this.appId = txt;
    },
    setSearchFormSlug(slug: string) {
      this.searchFormSlug = slug ?? "";
    },
    setBookingToken(token: string) {
      this.bookingToken = token;
    },
    saveLastFilters(filters) {
      this.lastFilters = filters;
    },
    saveSuccessfulSearchCache(
      fingerprint: string,
      day: string,
      snapshot: SearchState["lastSuccessfulSearchSnapshot"],
    ) {
      this.lastSuccessfulSearchFingerprint = fingerprint;
      this.lastSuccessfulSearchDay = day;
      this.lastSuccessfulSearchSnapshot = snapshot;
    },
    clearSuccessfulSearchCache() {
      this.lastSuccessfulSearchFingerprint = "";
      this.lastSuccessfulSearchDay = "";
      this.lastSuccessfulSearchSnapshot = null;
    },
    setPreferCachedSearchResults(value: boolean) {
      this.preferCachedSearchResults = value;
    },
    setSuppressSearchRemountNoise(value: boolean) {
      this.suppressSearchRemountNoise = value;
    },
    setSearchMAskChanged(bool: boolean) {
      this.hasSearchMaskChanged = bool;
      // Real user edits must leave comparison-return cache mode.
      if (bool) {
        this.preferCachedSearchResults = false;
        this.suppressSearchRemountNoise = false;
      }
    },
    clearSearchFieldValues(payload) {
      this.searchFieldValues = payload;
    },
    deleteFromVisibleSearchFields(typeToDelete) {
      const newVisibleSearchFields = {};
      for (const key in this.visibleSearchFields) {
        if (
          Object.prototype.hasOwnProperty.call(this.visibleSearchFields, key) &&
          key !== typeToDelete
        ) {
          newVisibleSearchFields[key] = this.visibleSearchFields[key];
        }
      }
      this.visibleSearchFields = newVisibleSearchFields;
    },
    async fetchSearchForm(payload) {
      const rootStore = useRootStore();
      const language = rootStore.language;
      const completedBy = rootStore.completedBy;
      const code = rootStore.code;

      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      const raw =
        typeof payload.searchQuery === "string"
          ? payload.searchQuery.trim()
          : "";
      const normalized = raw.replace(/^\?+/, "").replace(/^&+/, "");
      const filterSearchQuery = normalized ? `&${normalized}` : "";
      return client.getRequest(
        `${basePath}api/v1/search_forms/?application=${payload.appName}&language=${language}&completed_by=${completedBy}&code=${code}${filterSearchQuery}`,
      );
    },
    async fetchConsentModal(
      payload: ConsentModalPayload,
    ): Promise<ConsentModalResponse> {
      const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
      const basePath = isLocalEnv
        ? process.env.VUE_APP_TARIFF_MANAGER_URL
        : "/";
      const query = `application=${payload.appName}&modal_type=${payload.type}&white_label=${payload.whiteLabel}`;
      return client.getRequest(`${basePath}api/v1/modals/?${query}`);
    },
  },
});
